import { useEffect, useState, useRef } from "react";
import { getDateRange } from "../../pages/utils/dateUtils/filter_date";
import { useBotPhotos } from "../../hooks/useBotPhotos";
import BotAvatar from "../BotAvatar/BotAvatar";
import DateRangeModal from "./DateRangeModal";
import "./index.css";



export default function FiltersSection({
  startDate,
  endDate,
  onApplyFilters,
  bots = [],
  gateway = [],
  showGatewayFilter = true,
  showStatusFilter = false,
  showFluxoFilter = false,
  showStartsFilter = false,
  showPaymentStatusFilter = false,
  showPaymentMethodFilter = false,
  enableBotMultiSelect = false,
  compactBotFilter = false,
  showBotFilter = true,
  showDateFilter = true,
  showInputFilter = false,
  onSearchChange,
  searchPlaceholder = "Buscar por nome, sobrenome ou @username...",
}) {


  const bots2 = Array.isArray(bots) ? bots : [];
  const hasBots = bots2.length > 0;
  const photoUrls = useBotPhotos(bots2);

  // State declarations
  const [filters, setFilters] = useState({
    botId: "",
    gateway: "",
    status: "",
    fluxo: "todos",
    starts: "todos",
    paymentStatus: "",
    paymentMethod: "",
  });
  const [selectedBots, setSelectedBots] = useState([]);
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [allBots, setAllBots] = useState(true); // true = Todos os Bots
  const [botDropdownOpen, setBotDropdownOpen] = useState(false);
  const [gatewayDropdownOpen, setGatewayDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [fluxoDropdownOpen, setFluxoDropdownOpen] = useState(false);
  const [startsDropdownOpen, setStartsDropdownOpen] = useState(false);
  const [paymentStatusDropdownOpen, setPaymentStatusDropdownOpen] = useState(false);
  const [paymentMethodDropdownOpen, setPaymentMethodDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchInput, setSearchInput] = useState('');
  const [dateModalOpen, setDateModalOpen] = useState(false);

  const botDropdownRef = useRef(null);
  const gatewayDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const fluxoDropdownRef = useRef(null);
  const startsDropdownRef = useRef(null);
  const paymentStatusDropdownRef = useRef(null);
  const paymentMethodDropdownRef = useRef(null);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (botDropdownRef.current && !botDropdownRef.current.contains(event.target)) {
        setBotDropdownOpen(false);
      }
      if (gatewayDropdownRef.current && !gatewayDropdownRef.current.contains(event.target)) {
        setGatewayDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (fluxoDropdownRef.current && !fluxoDropdownRef.current.contains(event.target)) {
        setFluxoDropdownOpen(false);
      }
      if (startsDropdownRef.current && !startsDropdownRef.current.contains(event.target)) {
        setStartsDropdownOpen(false);
      }
      if (paymentStatusDropdownRef.current && !paymentStatusDropdownRef.current.contains(event.target)) {
        setPaymentStatusDropdownOpen(false);
      }
      if (paymentMethodDropdownRef.current && !paymentMethodDropdownRef.current.contains(event.target)) {
        setPaymentMethodDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce da busca: espera o usuário parar de digitar antes de filtrar.
  // Só dispara quando o texto MUDOU desde a última busca. (A versão anterior
  // pulava "a primeira execução", mas no StrictMode o efeito roda duas vezes na
  // montagem: a segunda passava e disparava uma busca vazia que recarregava a
  // lista — o GET duplicado/"piscada" de Disparos e Leads.)
  const lastSearchRef = useRef(searchInput);
  useEffect(() => {
    if (!onSearchChange) return;
    if (searchInput === lastSearchRef.current) return;

    const timeoutId = window.setTimeout(() => {
      lastSearchRef.current = searchInput;
      onSearchChange(searchInput);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    // Aplica filtros automaticamente ao montar, com todos os bots e data de hoje (Brasil)
    const range = getDateRange("today");
    setCustomStart(range.startDate);
    setCustomEnd(range.endDate);

    let timeoutId;

    if (!startDate && !endDate) {
      timeoutId = window.setTimeout(() => {
        onApplyFilters({
          ...range,
          botId: "",
          gatewayId: "",
          period: "today",
          customStart: range.startDate,
          customEnd: range.endDate,
        });
      }, 0);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  function handleChange(id, value) {
    setFilters(prev => ({
      ...prev,
      [id]: value,
    }));
  }

  const handleBotSelect = (botId) => {
    if (enableBotMultiSelect) {
      // Multi-select mode
      if (botId === "") {
        setSelectedBots([]);
        setAllBots(true);
      } else {
        setAllBots(false);
        setSelectedBots(prev =>
          prev.includes(botId)
            ? prev.filter(id => id !== botId)
            : [...prev, botId]
        );
      }
    } else {
      // Single select mode
      if (botId === "") {
        setAllBots(true);
        setFilters(prev => ({ ...prev, botId: "" }));
      } else {
        setAllBots(false);
        setFilters(prev => ({ ...prev, botId: botId }));
      }
      setBotDropdownOpen(false);
    }
  };

  const handlePeriodSelect = (value) => {
    if (value === "custom") {
      // Só ativa o período customizado quando o usuário confirmar no modal
      setDateModalOpen(true);
      return;
    }
    setPeriod(value);
    applyFiltersWithPeriod(value);
  };

  const handleCustomRangeApply = (range) => {
    setCustomStart(range.startDate);
    setCustomEnd(range.endDate);
    setPeriod("custom");
    setDateModalOpen(false);
    applyFiltersWithPeriod("custom", range);
  };

  const formatCustomRangeLabel = () => {
    if (!customStart || !customEnd) return "Personalizado";
    const fmt = (s) => {
      const [, m, d] = s.split("-");
      return `${d}/${m}`;
    };
    return customStart === customEnd ? fmt(customStart) : `${fmt(customStart)} - ${fmt(customEnd)}`;
  };

  const handleGatewaySelect = (value) => {
    handleChange("gateway", value);
    setGatewayDropdownOpen(false);
  };

  const getSelectedBotName = () => {
    if (enableBotMultiSelect) {
      if (selectedBots.length === 0) return "Todos os Bots";
      if (selectedBots.length === 1) {
        const bot = bots.find(b => b.id === selectedBots[0]);
        return bot ? bot.bot_username : "1 Bot";
      }
      return `${selectedBots.length} Bots`;
    } else {
      if (allBots || !filters.botId) return "Todos os Bots";
      const selectedBot = bots.find(b => b.id === parseInt(filters.botId));
      return selectedBot ? selectedBot.bot_username : "Todos os Bots";
    }
  };

  const getGatewayLabel = () => {
    if (!filters.gateway) return "Todos os Gateways";
    const selectedGateway = gateway.find(gw => gw.id.toString() === filters.gateway);
    return selectedGateway ? selectedGateway.gateway_name : "Todos os Gateways";
  };



  function applyFiltersWithPeriod(targetPeriod, overrideRange) {
    const botId = enableBotMultiSelect ? null : (allBots ? "" : filters.botId);
    const botIds = enableBotMultiSelect ? selectedBots : null;
    const gatewayId = filters.gateway;

    let range;
    if (targetPeriod === "custom") {
      range = overrideRange || {
        startDate: customStart,
        endDate: customEnd,
      };
    } else {
      range = getDateRange(targetPeriod);
    }

    // Envia tudo no body
    onApplyFilters({
      ...range,
      botId: botId || null,
      botIds: botIds || null,
      gatewayId: gatewayId || null,
      period: targetPeriod,
      customStart: range.startDate,
      customEnd: range.endDate,
      status: filters.status || null,
      fluxo: filters.fluxo !== 'todos' ? filters.fluxo : null,
      starts: filters.starts !== 'todos' ? filters.starts : null,
      paymentStatus: filters.paymentStatus || null,
      paymentMethod: filters.paymentMethod || null,
    });
  }

  function applyFilters() {
    applyFiltersWithPeriod(period);
  }

  // Quantos filtros (além do botão Filtrar) aparecem no grid — usado pra decidir
  // se o botão Filtrar deve ocupar a linha inteira no mobile ou só metade
  // (ex: Estatísticas só mostra o filtro de Bot, então 1/2 já fica bonito).
  const visibleFilterCount = [
    showGatewayFilter,
    showStatusFilter,
    showFluxoFilter,
    showStartsFilter,
    showPaymentStatusFilter,
    showPaymentMethodFilter,
    showBotFilter,
  ].filter(Boolean).length;

  return (
    <>
      {showDateFilter && (
        <div className="filters-date">
          <div className="flex flex-wrap justify-center md:justify-end gap-1.5 md:gap-2">
            <button
              className={`period-btn ${period === 'today' ? 'active' : ''}`}
              onClick={() => handlePeriodSelect('today')}
            >
              Hoje
            </button>
            <button
              className={`period-btn ${period === 'yesterday' ? 'active' : ''}`}
              onClick={() => handlePeriodSelect('yesterday')}
            >
              Ontem
            </button>
            <button
              className={`period-btn ${period === '7days' ? 'active' : ''}`}
              onClick={() => handlePeriodSelect('7days')}
            >
              Semana
            </button>
            <button
              className={`period-btn ${period === 'thisMonth' ? 'active' : ''}`}
              onClick={() => handlePeriodSelect('thisMonth')}
            >
              Mês
            </button>
            <button
              className={`period-btn ${period === 'max' ? 'active' : ''}`}
              onClick={() => handlePeriodSelect('max')}
            >
              Tudo
            </button>
            <button
              className={`period-btn period-btn-custom ${period === 'custom' ? 'active' : ''}`}
              onClick={() => handlePeriodSelect('custom')}
            >
              <i className="fas fa-calendar-days" style={{ marginRight: 6 }} />
              {period === 'custom' ? formatCustomRangeLabel() : 'Personalizado'}
            </button>
          </div>
        </div>
      )}

      <DateRangeModal
        open={dateModalOpen}
        initialStart={customStart}
        initialEnd={customEnd}
        onApply={handleCustomRangeApply}
        onClose={() => setDateModalOpen(false)}
      />
      <div className="filters-section" style={{ marginTop: 0 }}>
        {/* Period Buttons */}


        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>

          {/* Search Input - ocupa 50% do filtro */}
          {showInputFilter && (
            <div className="filter-group" style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '0 0 20%', minWidth: 0 }}>
              <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#888', pointerEvents: 'none' }} />
              <input
                type="text"
                className="filter-input"
                style={{ paddingLeft: 42 }}
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          )}

        <div className={`filters-grid ${visibleFilterCount <= 1 ? 'filters-grid-single' : ''}`} style={{ flex: showInputFilter && !isMobile ? '1 1 70%' : '1 1 100%', minWidth: 0, ...(compactBotFilter && !isMobile ? { display: 'flex', gap: 20, justifyContent: 'flex-start', alignItems: 'flex-end' } : {}) }}>

          {/* Gateway */}
          {showGatewayFilter && (
            <div className="filter-group" ref={gatewayDropdownRef} style={{ position: 'relative' }}>
              <div
                className={`filter-dropdown-trigger ${gatewayDropdownOpen ? 'open' : ''}`}
                onClick={() => setGatewayDropdownOpen(!gatewayDropdownOpen)}
              >
                <span>{getGatewayLabel()}</span>
                <i className="fas fa-chevron-down" />
              </div>

              {gatewayDropdownOpen && (
                <div className="filter-dropdown-container">
                  <div
                    className={`filter-dropdown-item ${!filters.gateway ? 'selected' : ''}`}
                    onClick={() => handleGatewaySelect("")}
                  >
                    Todos os Gateways
                  </div>
                  {gateway.map((gw) => (
                    <div
                      key={gw.id}
                      className={`filter-dropdown-item ${filters.gateway === gw.id.toString() ? 'selected' : ''}`}
                      onClick={() => handleGatewaySelect(gw.id.toString())}
                    >
                      {gw.gateway_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Status Filter */}
          {showStatusFilter && (
            <div className="filter-group" ref={statusDropdownRef} style={{ position: 'relative' }}>
              <div
                className={`filter-dropdown-trigger ${statusDropdownOpen ? 'open' : ''}`}
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
              >
                <span>{filters.status || "Todos os Status"}</span>
                <i className="fas fa-chevron-down" />
              </div>

              {statusDropdownOpen && (
                <div className="filter-dropdown-container">
                  <div className={`filter-dropdown-item ${!filters.status ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: "" })); setStatusDropdownOpen(false); }}>Todos</div>
                  <div className={`filter-dropdown-item ${filters.status === 'novo' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: "novo" })); setStatusDropdownOpen(false); }}>Novo</div>
                  <div className={`filter-dropdown-item ${filters.status === 'pendente' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: "pendente" })); setStatusDropdownOpen(false); }}>Pendente</div>
                  <div className={`filter-dropdown-item ${filters.status === 'pago' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: "pago" })); setStatusDropdownOpen(false); }}>Pago</div>
                  <div className={`filter-dropdown-item ${filters.status === 'bloqueado' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, status: "bloqueado" })); setStatusDropdownOpen(false); }}>Bloqueado</div>
                </div>
              )}
            </div>
          )}

          {/* Fluxo Filter */}
          {showFluxoFilter && (
            <div className="filter-group" ref={fluxoDropdownRef} style={{ position: 'relative' }}>
              <div
                className={`filter-dropdown-trigger ${fluxoDropdownOpen ? 'open' : ''}`}
                onClick={() => setFluxoDropdownOpen(!fluxoDropdownOpen)}
              >
                <span>{filters.fluxo === 'todos' ? "Todos os Fluxos" : "Fluxo Padrão"}</span>
                <i className="fas fa-chevron-down" />
              </div>

              {fluxoDropdownOpen && (
                <div className="filter-dropdown-container">
                  <div className={`filter-dropdown-item ${filters.fluxo === 'todos' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, fluxo: "todos" })); setFluxoDropdownOpen(false); }}>Todos os Fluxos</div>
                  <div className={`filter-dropdown-item ${filters.fluxo === 'padrão' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, fluxo: "padrão" })); setFluxoDropdownOpen(false); }}>Fluxo Padrão</div>
                </div>
              )}
            </div>
          )}

          {/* Starts Filter */}
          {showStartsFilter && (
            <div className="filter-group" ref={startsDropdownRef} style={{ position: 'relative' }}>
              <div
                className={`filter-dropdown-trigger ${startsDropdownOpen ? 'open' : ''}`}
                onClick={() => setStartsDropdownOpen(!startsDropdownOpen)}
              >
                <span>{filters.starts === 'todos' ? "Todos os Starts" : `+${filters.starts} Starts`}</span>
                <i className="fas fa-chevron-down" />
              </div>

              {startsDropdownOpen && (
                <div className="filter-dropdown-container">
                  <div className={`filter-dropdown-item ${filters.starts === 'todos' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, starts: "todos" })); setStartsDropdownOpen(false); }}>Todos</div>
                  <div className={`filter-dropdown-item ${filters.starts === '1' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, starts: "1" })); setStartsDropdownOpen(false); }}>1 Start</div>
                  <div className={`filter-dropdown-item ${filters.starts === '2' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, starts: "2" })); setStartsDropdownOpen(false); }}>+2 Starts</div>
                  <div className={`filter-dropdown-item ${filters.starts === '3' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, starts: "3" })); setStartsDropdownOpen(false); }}>+3 Starts</div>
                  <div className={`filter-dropdown-item ${filters.starts === '5' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, starts: "5" })); setStartsDropdownOpen(false); }}>+5 Starts</div>
                  <div className={`filter-dropdown-item ${filters.starts === '10' ? 'selected' : ''}`} onClick={() => { setFilters(prev => ({ ...prev, starts: "10" })); setStartsDropdownOpen(false); }}>+10 Starts</div>
                </div>
              )}
            </div>
          )}

          {/* Payment Status Filter (Financeiro) */}
          {showPaymentStatusFilter && (() => {
            const psLabels = { '': 'Todos os Status', approved: 'Pago', pending: 'Pendente', expired: 'Expirado', failed: 'Falhou' };
            return (
              <div className="filter-group" ref={paymentStatusDropdownRef} style={{ position: 'relative' }}>
                <div
                  className={`filter-dropdown-trigger ${paymentStatusDropdownOpen ? 'open' : ''}`}
                  onClick={() => setPaymentStatusDropdownOpen(!paymentStatusDropdownOpen)}
                >
                  <span>{psLabels[filters.paymentStatus] || 'Todos os Status'}</span>
                  <i className="fas fa-chevron-down" />
                </div>
                {paymentStatusDropdownOpen && (
                  <div className="filter-dropdown-container">
                    {Object.entries(psLabels).map(([value, label]) => (
                      <div
                        key={value || 'all'}
                        className={`filter-dropdown-item ${filters.paymentStatus === value ? 'selected' : ''}`}
                        onClick={() => { setFilters(prev => ({ ...prev, paymentStatus: value })); setPaymentStatusDropdownOpen(false); }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Payment Method Filter (PIX x Cartão) */}
          {showPaymentMethodFilter && (() => {
            const pmLabels = { '': 'Todos os Métodos', pix: 'PIX', card: 'Cartão' };
            return (
              <div className="filter-group" ref={paymentMethodDropdownRef} style={{ position: 'relative' }}>
                <div
                  className={`filter-dropdown-trigger ${paymentMethodDropdownOpen ? 'open' : ''}`}
                  onClick={() => setPaymentMethodDropdownOpen(!paymentMethodDropdownOpen)}
                >
                  <span>{pmLabels[filters.paymentMethod] || 'Todos os Métodos'}</span>
                  <i className="fas fa-chevron-down" />
                </div>
                {paymentMethodDropdownOpen && (
                  <div className="filter-dropdown-container">
                    {Object.entries(pmLabels).map(([value, label]) => (
                      <div
                        key={value || 'all'}
                        className={`filter-dropdown-item ${filters.paymentMethod === value ? 'selected' : ''}`}
                        onClick={() => { setFilters(prev => ({ ...prev, paymentMethod: value })); setPaymentMethodDropdownOpen(false); }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Bots */}
          {showBotFilter && (
            <div className="filter-group" ref={botDropdownRef} style={{ position: 'relative', ...(compactBotFilter && !isMobile ? { flex: '0 0 340px', minWidth: 220 } : {}) }}>
              <div
                className={`filter-dropdown-trigger ${botDropdownOpen ? 'open' : ''}`}
                onClick={() => setBotDropdownOpen(!botDropdownOpen)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {!allBots && filters.botId && (() => {
                    const bot = bots2.find(b => b.id.toString() === filters.botId.toString());
                    return bot ? <BotAvatar photoUrl={photoUrls[bot.id]} size={22} /> : null;
                  })()}
                  {getSelectedBotName()}
                </span>
                <i className="fas fa-chevron-down" />
              </div>

              {botDropdownOpen && (
                <div className="filter-dropdown-container">
                  <div
                    className={`filter-dropdown-item ${(enableBotMultiSelect ? selectedBots.length === 0 : allBots) ? 'selected' : ''}`}
                    onClick={() => handleBotSelect("")}
                  >
                    <i className="fas fa-layer-group mr-4" />
                    Todos os Bots
                  </div>
                  {bots2.map((bot) => {
                    const isActive = String(bot.id) === String(filters.botId);
                    const isInactive = bot?.is_active === false;

                    return (
                      <div
                        key={bot.id}
                        className={`filter-dropdown-item ${enableBotMultiSelect
                            ? (selectedBots.includes(bot.id) ? 'selected' : '')
                            : (filters.botId === bot.id.toString() ? 'selected' : '')
                          } ${isInactive ? 'inactive' : ''}`}
                        onClick={() =>
                          handleBotSelect(
                            enableBotMultiSelect ? bot.id : bot.id.toString()
                          )
                        }
                        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <BotAvatar photoUrl={photoUrls[bot.id]} size={28} />
                        <span>
                          {bot.bot_username}
                          {isInactive && " - Inativo"}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}


          <div className="filter-submit-group">
            <button className="btn-primary-filter" onClick={applyFilters}>
              <i className="fas fa-filter" />
              Filtrar
            </button>
          </div>

        </div>
        </div>


      </div></>
  );
}
